import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

/* ═══════════════════════════════════════════════
   MENU PORTAIL — Rendu dans document.body
   Se positionne sous le bouton "..." et se ferme au scroll
   ═══════════════════════════════════════════════ */
const MenuPortal = ({ buttonRef, menuRef, colorKey, s, onColorChange, onRename, onDelete, album, onClose }) => {
  const [pos, setPos] = useState(null);

  const updatePos = useCallback(() => {
    if (!buttonRef?.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
  }, [buttonRef]);

  useEffect(() => {
    updatePos();
    const closeOnScroll = () => onClose();
    const containers = document.querySelectorAll('[data-scroll-container]');
    window.addEventListener('scroll', closeOnScroll, true);
    containers.forEach(c => c.addEventListener('scroll', closeOnScroll));
    window.addEventListener('resize', closeOnScroll);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      containers.forEach(c => c.removeEventListener('scroll', closeOnScroll));
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [updatePos, onClose]);

  if (!pos) return null;

  return (
    <div ref={menuRef} data-testid={`album-menu-${album.id}`} style={{
      position: 'fixed', top: pos.top, left: pos.left, transform: 'translateX(-50%)',
      zIndex: 9999, minWidth: 180, backgroundColor: '#0C0C0C',
      border: '1px solid rgba(190,155,45,0.25)', borderRadius: 10,
      boxShadow: '0 8px 32px rgba(0,0,0,0.7), 0 0 0 1px rgba(190,155,45,0.08)',
      padding: '6px 0', fontFamily: "'Georgia',serif",
    }}>
      {/* Couleurs */}
      <div style={{ display: 'flex', gap: 5, padding: '8px 10px 6px', justifyContent: 'center', flexWrap: 'wrap',
        borderBottom: '1px solid rgba(190,155,45,0.1)', marginBottom: 4 }}>
        {Object.entries(COVER_COLORS).map(([key, val]) => (
          <button key={key} data-testid={`color-${key}`}
            onClick={(e) => { e.stopPropagation(); onColorChange?.(key); onClose(); }}
            title={val.name}
            style={{
              width: s.dotSize, height: s.dotSize, borderRadius: '50%', cursor: 'pointer', backgroundColor: val.dot, flexShrink: 0,
              border: colorKey === key ? '2.5px solid #C5A028' : '1px solid rgba(190,155,45,0.2)',
              transform: colorKey === key ? 'scale(1.3)' : 'scale(1)',
              boxShadow: colorKey === key ? '0 0 6px rgba(190,155,45,0.25)' : 'none',
              transition: 'transform 0.15s, border-color 0.2s',
            }}
            onMouseEnter={e => { if (colorKey !== key) e.currentTarget.style.transform = 'scale(1.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = colorKey === key ? 'scale(1.3)' : 'scale(1)'; }}
          />
        ))}
      </div>
      {/* Renommer */}
      <button data-testid={`album-rename-${album.id}`}
        onClick={(e) => { e.stopPropagation(); onClose(); onRename?.(album); }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
          backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 6,
          fontFamily: "'Georgia',serif", fontSize: 12, color: '#FAF3E0', textAlign: 'left' }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(190,155,45,0.1)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C5A028" strokeWidth="1.5"><path d="M17 3a2.83 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
        Renommer
      </button>
      {/* Supprimer */}
      <button data-testid={`album-delete-${album.id}`}
        onClick={(e) => { e.stopPropagation(); onClose(); onDelete?.(album); }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
          backgroundColor: 'transparent', border: 'none', cursor: 'pointer', borderRadius: 6,
          fontFamily: "'Georgia',serif", fontSize: 12, color: 'rgba(220,80,80,0.85)', textAlign: 'left' }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(180,60,60,0.1)'}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
        Supprimer
      </button>
    </div>
  );
};

const COVER_COLORS = {
  noir:        { name: 'Noir',           dot: '#1a1a1a', base: '#0C0C0C', accent: '#111111' },
  or:          { name: 'Or',             dot: '#C5A028', base: '#302008', accent: '#38260A' },
  rose:        { name: 'Rose',           dot: '#D4247B', base: '#5E0A30', accent: '#520828' },
  bleu:        { name: 'Bleu',           dot: '#1E3FA0', base: '#0A1535', accent: '#0C1830' },
  vert:        { name: 'Vert',           dot: '#1A6B40', base: '#0A2818', accent: '#0C2A1C' },
  marron:      { name: 'Marron',         dot: '#6B4226', base: '#2A1508', accent: '#30190A' },
  ivoire:      { name: 'Ivoire',         dot: '#E8DCC8', base: '#FDFDF8', accent: '#F8F4EC', light: true, overlay: 'roses_ivoire' },
  or_vegetal:  { name: 'Or Vegetal',     dot: '#B8962E', base: '#0A0A08', accent: '#0C0C0A', premium: true, overlay: 'or_vegetal' },
  fleurs_nuit: { name: 'Fleurs de Nuit', dot: '#2E5E3E', base: '#060806', accent: '#080A08', premium: true, overlay: 'fleurs_nuit' },
};

/* ═══════════════════════════════════════════════
   CORNIÈRE — Image IA dorée réaliste
   ═══════════════════════════════════════════════ */
const GoldCorner = ({ pos, size = 28, isLightBg = false }) => {
  const rotations = { tl: 'rotate(0deg)', tr: 'rotate(90deg)', bl: 'rotate(-90deg)', br: 'rotate(180deg)' };
  const off = 0;
  const imgSrc = isLightBg ? '/covers/gold_corner_light.png' : '/covers/gold_corner.png';
  return (
    <div style={{
      position: 'absolute', pointerEvents: 'none', zIndex: 5,
      width: size, height: size,
      top: pos === 'tl' || pos === 'tr' ? off : 'auto',
      bottom: pos === 'bl' || pos === 'br' ? off : 'auto',
      left: pos === 'tl' || pos === 'bl' ? off : 'auto',
      right: pos === 'tr' || pos === 'br' ? off : 'auto',
      transform: rotations[pos],
      mixBlendMode: isLightBg ? 'multiply' : 'screen',
      filter: isLightBg ? 'saturate(1.3) contrast(1.1)' : 'brightness(1.5) saturate(1.4)',
    }}>
      <img src={imgSrc} alt="" 
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        onError={(e) => { e.target.style.display = 'none'; }} />
    </div>
  );
};

const AlbumBook = ({ album, onClick, size = 'md', isActive, onColorChange, onRename, onDelete, token, coverImageSrc }) => {
  const colorKey = album.cover_color && COVER_COLORS[album.cover_color] ? album.cover_color : 'noir';
  const color = COVER_COLORS[colorKey];
  const isLight = color.light;
  const [animating, setAnimating] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [hovered, setHovered] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  const s = size === 'xs'
    ? { w: 95, h: 130, font: 9, seal: 40, spine: 9, dotSize: 13, corner: 18, border: 5 }
    : size === 'sm'
    ? { w: 130, h: 175, font: 11, seal: 56, spine: 12, dotSize: 18, corner: 26, border: 7 }
    : size === 'lg'
    ? { w: 210, h: 280, font: 16, seal: 84, spine: 16, dotSize: 22, corner: 42, border: 10 }
    : size === 'xl'
    ? { w: 320, h: 430, font: 22, seal: 200, spine: 22, dotSize: 28, corner: 60, border: 14 }
    : { w: 170, h: 228, font: 13, seal: 70, spine: 14, dotSize: 20, corner: 34, border: 9 };

  const coverOpen = isActive || animating;

  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [showMenu]);

  const handleBookClick = () => {
    if (showMenu) return;
    if (isActive) { onClick?.(); return; }
    setAnimating(true);
    setTimeout(() => { onClick?.(); setAnimating(false); }, 900);
  };

  /* Overlay image path for premium editions */
  const overlayImg = color.overlay === 'fleurs_nuit' ? '/covers/fleurs_nuit.jpg'
    : color.overlay === 'or_vegetal' ? '/covers/or_vegetal.jpg'
    : color.overlay === 'roses_ivoire' ? '/covers/roses_ivoire.jpg' : null;

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 4, position: 'relative' }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <div data-testid={`album-book-${album.id}`} onClick={handleBookClick}
        style={{
          width: s.w, height: s.h, position: 'relative', cursor: 'pointer', perspective: '1000px',
          transition: 'transform 0.3s ease',
          transform: hovered && !coverOpen ? 'translateY(-6px) scale(1.03)' : 'translateY(0) scale(1)',
          filter: hovered && !coverOpen ? 'brightness(1.06)' : 'brightness(1)',
        }}>

        {/* ═══ PAGE INTÉRIEURE ═══ */}
        <div style={{
          position: 'absolute', left: s.spine + 1, top: 2, right: 1, bottom: 2,
          background: 'linear-gradient(135deg, #FAF6EE 0%, #F5EFDF 40%, #FAF6EE 70%, #F0EAD6 100%)',
          borderRadius: '0 4px 4px 0', boxShadow: 'inset 3px 0 10px rgba(0,0,0,0.1)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '12px 8px',
        }}>
          {/* Filet doré intérieur */}
          <div style={{ position: 'absolute', inset: 8, border: '0.5px solid rgba(212,175,55,0.22)', borderRadius: 2 }} />

          {/* Médaillon central — photo de couverture en cercle, ou logo Bobine en fallback */}
          {(coverImageSrc || (album.cover_photo_id && token)) ? (
            <div style={{
              width: s.seal * 0.62, height: s.seal * 0.62,
              borderRadius: '50%', overflow: 'hidden',
              border: '2px solid rgba(212,175,55,0.55)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.35), 0 0 24px rgba(212,175,55,0.18), inset 0 0 0 1px rgba(255,255,255,0.15)',
              marginBottom: size === 'sm' ? 4 : 8,
              flexShrink: 0,
            }}>
              <img
                src={coverImageSrc || `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api/photos/${album.cover_photo_id}/thumbnail?token=${token}`}
                alt=""
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = 'rgba(212,175,55,0.1)'; }}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          ) : (
            <img src="/bobine-gold-reel.png" alt="Bobine"
              onError={(e) => { e.target.src = '/bobine-reel-transparent.png'; }}
              style={{
                width: s.seal * 0.6, height: s.seal * 0.6, objectFit: 'contain',
                marginBottom: size === 'sm' ? 4 : 8,
                filter: 'drop-shadow(0 2px 6px rgba(160,130,40,0.15))',
              }} />
          )}

          {/* Titre de l'album — voile sombre derrière pour la lisibilité quand photo de cover */}
          <div style={{
            position: 'relative', maxWidth: '92%',
            ...((coverImageSrc || (album.cover_photo_id && token)) ? {
              padding: '3px 10px', borderRadius: 8,
              background: 'linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.75))',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            } : {}),
          }}>
            <span style={{
              fontFamily: "'Georgia',serif", fontSize: Math.max(s.font - 1, 10), fontWeight: 700,
              color: (coverImageSrc || (album.cover_photo_id && token)) ? '#F7E6BD' : '#B8962E',
              textAlign: 'center', display: 'block',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              letterSpacing: 0.5, lineHeight: 1.3,
              textShadow: (coverImageSrc || (album.cover_photo_id && token)) ? '0 1px 3px rgba(0,0,0,0.7)' : 'none',
            }}>{album.name}</span>
          </div>
          {/* Message poétique */}
          <span style={{
            fontFamily: "'Georgia',serif", fontSize: Math.max(s.font - 4, 7),
            color: (coverImageSrc || (album.cover_photo_id && token)) ? 'rgba(247,230,189,0.7)' : '#C5A028',
            fontStyle: 'italic', textAlign: 'center',
            marginTop: size === 'sm' ? 2 : 4, letterSpacing: 0.3, opacity: 0.85,
            textShadow: (coverImageSrc || (album.cover_photo_id && token)) ? '0 1px 2px rgba(0,0,0,0.7)' : 'none',
          }}>Le fil de votre memoire</span>
        </div>

        {/* ═══ TRANCHE 3D BISEAUTÉE ═══ */}
        <div style={{
          position: 'absolute', left: 0, top: -1, bottom: -1, width: s.spine,
          background: `linear-gradient(90deg,
            #5A4818 0%, #7A6425 10%, #A08230 25%, #B8962E 40%,
            #C5A028 50%,
            #B8962E 60%, #A08230 75%, #7A6425 90%, #5A4818 100%)`,
          borderRadius: '4px 0 0 4px',
          boxShadow: `
            3px 0 8px rgba(0,0,0,0.7),
            inset -2px 0 3px rgba(0,0,0,0.35),
            inset 1px 0 2px rgba(255,235,150,0.15),
            -1px 0 2px rgba(0,0,0,0.5)`,
          zIndex: 4,
        }}>
          {/* Lignes de pages sur la tranche */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '4px 0 0 4px',
            backgroundImage: `repeating-linear-gradient(180deg,
              transparent, transparent 2.5px,
              rgba(0,0,0,0.15) 2.5px, rgba(0,0,0,0.15) 3px)`,
          }}/>
          {/* Reflet central */}
          <div style={{ position: 'absolute', top: '5%', bottom: '5%', left: '35%', width: '30%',
            background: 'linear-gradient(180deg, rgba(255,235,150,0.1) 0%, rgba(255,235,150,0.03) 50%, rgba(255,235,150,0.1) 100%)',
          }}/>
        </div>

        {/* ═══ ÉPAISSEUR DU LIVRE (pages empilées) ═══ */}
        <div style={{
          position: 'absolute', left: s.spine - 1, top: 3, bottom: 3, width: 4,
          background: 'linear-gradient(90deg, #E8DCC8, #F5EFDF, #E8DCC8)',
          boxShadow: 'inset 0 0 2px rgba(0,0,0,0.15)',
          zIndex: 3,
        }}/>

        {/* ═══ COUVERTURE PRINCIPALE ═══ */}
        <div style={{
          position: 'absolute', left: s.spine, top: 0, right: 0, bottom: 0,
          borderRadius: '0 5px 5px 0',
          transformOrigin: 'left center',
          transform: coverOpen ? 'rotateY(-85deg)' : 'rotateY(0deg)',
          transition: coverOpen ? 'transform 0.9s cubic-bezier(0.22,0.61,0.36,1)' : 'transform 0.6s cubic-bezier(0.55,0,0.68,0.53)',
          zIndex: 3, backfaceVisibility: 'hidden',
          overflow: 'visible',
        }}>
          {/* Fond de la couverture */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '0 5px 5px 0',
            backgroundColor: color.base,
            overflow: 'hidden',
          }}>
            {/* Texture cuir subtile via noise */}
            <div style={{ position: 'absolute', inset: 0,
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
              backgroundSize: '128px 128px',
            }} />

            {/* Éclairage de surface — très discret pour les couleurs foncées */}
            <div style={{ position: 'absolute', inset: 0,
              background: isLight
                ? `radial-gradient(ellipse at 35% 25%, rgba(255,255,255,0.08) 0%, transparent 50%),
                   radial-gradient(ellipse at 80% 90%, rgba(0,0,0,0.06) 0%, transparent 45%)`
                : `radial-gradient(ellipse at 30% 20%, ${color.accent} 0%, transparent 50%),
                   radial-gradient(ellipse at 85% 90%, rgba(0,0,0,0.2) 0%, transparent 45%)`,
            }} />

            {/* ═══ OVERLAY TEXTURE IA (Editions Limitees) ═══ */}
            {overlayImg && (
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${overlayImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: isLight ? 0.85 : (color.overlay === 'fleurs_nuit' ? 0.7 : 0.75),
                mixBlendMode: isLight ? 'multiply' : (color.overlay === 'fleurs_nuit' ? 'lighten' : 'screen'),
              }} />
            )}
          </div>

          {/* ═══ OMBRAGE 3D — Biseau complet ═══ */}
          <div style={{
            position: 'absolute', inset: 0, borderRadius: '0 5px 5px 0', pointerEvents: 'none',
            boxShadow: coverOpen
              ? '8px 8px 30px rgba(0,0,0,0.8), 2px 0 8px rgba(0,0,0,0.5)'
              : `4px 6px 20px rgba(0,0,0,0.65),
                 1px 1px 4px rgba(0,0,0,0.4),
                 inset 0 1px 0 rgba(255,255,255,0.08),
                 inset 0 -1px 0 rgba(0,0,0,0.2),
                 inset -1px 0 0 rgba(0,0,0,0.15),
                 inset 1px 0 0 rgba(255,255,255,0.04)`,
          }}/>
          {/* Bordure supérieure — reflet lumière */}
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 2, borderRadius: '0 5px 0 0',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0.03) 70%, transparent)',
            pointerEvents: 'none',
          }}/>
          {/* Bordure droite — ombre de profondeur */}
          <div style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 3, borderRadius: '0 5px 5px 0',
            background: 'linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.08))',
            pointerEvents: 'none',
          }}/>

          {/* ═══ DOUBLE FILET DORÉ ═══ */}
          <div style={{ position: 'absolute', inset: s.border,
            border: `1.5px solid rgba(200,170,55,${isLight ? '0.4' : '0.6'})`, borderRadius: 2, pointerEvents: 'none', zIndex: 1,
            boxShadow: `inset 0 0 4px rgba(212,175,55,0.06),
                        0 0 3px rgba(212,175,55,0.1)` }} />
          <div style={{ position: 'absolute', inset: s.border + 4,
            border: `0.5px solid rgba(200,170,55,${isLight ? '0.2' : '0.3'})`, borderRadius: 1, pointerEvents: 'none', zIndex: 1 }} />

          {/* ═══ CORNIÈRES EN OR CISELÉ ═══ */}
          <GoldCorner pos="tl" size={s.corner} isLightBg={isLight} />
          <GoldCorner pos="tr" size={s.corner} isLightBg={isLight} />
          <GoldCorner pos="bl" size={s.corner} isLightBg={isLight} />
          <GoldCorner pos="br" size={s.corner} isLightBg={isLight} />

          {/* ═══ SCEAU BOBINE — Cercle doré scintillant (ou photo de couverture si définie) ═══ */}
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -55%)',
            width: s.seal, height: s.seal, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
          }}>
            {/* Cercle doré extérieur — or vif scintillant */}
            <div style={{
              position: 'absolute', inset: -4, borderRadius: '50%',
              border: '2px solid #C5A028',
              boxShadow: `
                inset 0 2px 4px rgba(0,0,0,0.4),
                inset 0 -1px 3px rgba(212,175,55,0.2),
                0 0 8px rgba(197,160,40,0.3),
                0 0 15px rgba(197,160,40,0.1)`,
            }} />
            {(coverImageSrc || (album.cover_photo_id && token)) ? (
              /* Médaillon photo de couverture — dans un cercle doré */
              <div style={{
                position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -2px 4px rgba(0,0,0,0.4)',
              }}>
                <img
                  src={coverImageSrc || `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api/photos/${album.cover_photo_id}/thumbnail?token=${token}`}
                  alt=""
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.parentElement) e.target.parentElement.style.background = 'radial-gradient(circle at 45% 40%, #1A1A1A 0%, #0C0C0C 60%, #080808 100%)';
                  }}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </div>
            ) : (
              <>
                {/* Fond noir velours du sceau */}
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: `radial-gradient(circle at 45% 40%,
                    #1A1A1A 0%, #0C0C0C 60%, #080808 100%)`,
                  boxShadow: `inset 0 2px 6px rgba(0,0,0,0.6), inset 0 -1px 2px rgba(212,175,55,0.08)`,
                }} />
                {/* Logo Bobine — or brillant scintillant */}
                <img src="/bobine-face-logo.png" alt="B"
                  onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'block'; }}
                  style={{
                    width: s.seal * 0.8, height: s.seal * 0.8, objectFit: 'contain',
                    position: 'relative', zIndex: 1,
                    filter: `sepia(1) saturate(2.5) brightness(1.1) contrast(1.1) hue-rotate(-5deg)
                      drop-shadow(0 0 4px rgba(212,175,55,0.4))
                      drop-shadow(0 1px 1px rgba(0,0,0,0.5))`,
                    opacity: 0.95,
                  }} />
                <span style={{
                  display: 'none', fontFamily: "'Georgia',serif", fontSize: s.seal * 0.4, fontWeight: 700,
                  color: '#D4AF37', textShadow: '0 0 6px rgba(212,175,55,0.4), 0 1px 0 rgba(80,65,15,0.6)',
                }}>B</span>
              </>
            )}
          </div>

          {/* Badge Édition Limitée */}
          {color.premium && (
            <div style={{ position: 'absolute', top: s.border + 8, left: 0, right: 0, textAlign: 'center', zIndex: 2 }}>
              <span style={{
                fontFamily: "'Georgia',serif", fontSize: Math.max(s.font - 4, 7),
                color: 'rgba(180,150,45,0.5)', letterSpacing: 1.5, textTransform: 'uppercase', fontStyle: 'italic',
              }}>Edition Limitee</span>
            </div>
          )}

          {/* ═══ TITRE — or antique ═══ */}
          <div style={{ position: 'absolute', bottom: s.border + 8, left: 0, right: 0, textAlign: 'center', padding: '0 14px', zIndex: 2 }}>
            <span style={{
              fontFamily: "'Georgia',serif", fontSize: s.font, fontWeight: 700,
              color: isLight ? '#7A6020' : '#C5A028',
              textShadow: isLight ? 'none' : '0 1px 2px rgba(0,0,0,0.7)',
              letterSpacing: 0.8, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{album.name}</span>
            <span style={{
              fontFamily: "'Georgia',serif", fontSize: Math.max(s.font - 4, 8),
              color: isLight ? '#7A6020' : '#D4AF37',
              letterSpacing: 0.5, fontStyle: 'italic',
            }}>{album.photo_count || 0} photo{(album.photo_count || 0) !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* ═══ OMBRE PORTÉE GLOBALE ═══ */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, borderRadius: 5,
          boxShadow: '0 10px 30px rgba(0,0,0,0.55), 0 3px 10px rgba(0,0,0,0.35)' }} />
      </div>

      {/* Bouton "..." */}
      <button ref={btnRef} data-testid={`album-options-${album.id}`}
        onClick={(e) => { e.stopPropagation(); setShowMenu(prev => !prev); }}
        style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 10px',
          display: 'flex', alignItems: 'center', opacity: hovered || showMenu ? 0.85 : 0.55, transition: 'opacity 0.25s' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#C5A028" stroke="none">
          <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
        </svg>
      </button>

      {/* Menu contextuel — portail React dans document.body */}
      {showMenu && ReactDOM.createPortal(
        <>
          <div onClick={() => setShowMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
          <MenuPortal buttonRef={btnRef} menuRef={menuRef} colorKey={colorKey} s={s}
            onColorChange={onColorChange} onRename={onRename} onDelete={onDelete}
            album={album} onClose={() => setShowMenu(false)} />
        </>,
        document.body
      )}
    </div>
  );
};

export { COVER_COLORS };
export default AlbumBook;
